<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Shopee · Repack"
    title="Create Repack Carton"
    [subtitle]="'Create a new carton for order ' + (originalShopeeOrderNo || 'Unknown') + '. Enter actual physical dimensions below.'"
  >
    <div actions>
      <button hlmBtn variant="outline" size="sm" (click)="goBack()">
        <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-3.5" />
        Back
      </button>
    </div>
  </app-page-header>

  <!-- Feedback alerts -->
  @if (successMessage) {
    <app-inline-alert tone="success" [message]="successMessage" />
  }
  @if (errorMessage) {
    <app-inline-alert tone="danger" [message]="errorMessage" />
  }

  <!-- Order Reference Card -->
  <div hlmCard>
    <div class="px-4 py-3 border-b border-border">
      <h2 class="text-sm font-semibold text-foreground">Order Reference</h2>
    </div>
    <div class="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Original Order ID</p>
        <p class="font-mono text-sm font-medium text-foreground">{{ originalOrderId || 'N/A' }}</p>
      </div>
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Shopee Order No</p>
        <p class="font-mono text-sm font-medium text-foreground">{{ originalShopeeOrderNo || 'N/A' }}</p>
      </div>
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Service Code</p>
        <p class="text-sm text-foreground">{{ newCarton.service_code || 'N/A' }}</p>
      </div>
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Destination Region</p>
        <p class="text-sm text-foreground">{{ newCarton.destination_region || 'N/A' }}</p>
      </div>
    </div>
  </div>

  <!-- Associated Parcels Card -->
  <div hlmCard>
    <div class="px-4 py-3 border-b border-border flex items-center justify-between">
      <h2 class="text-sm font-semibold text-foreground">Associated Parcels</h2>
      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30">
        {{ parcelsToRepack.length }}
      </span>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
            <th class="text-left px-4 py-2.5 font-medium">#</th>
            <th class="text-left px-4 py-2.5 font-medium">Tracking Number</th>
            <th class="text-left px-4 py-2.5 font-medium">Weight</th>
            <th class="text-left px-4 py-2.5 font-medium">Dimensions (L×W×H)</th>
          </tr>
        </thead>
        <tbody>
          @for (parcel of parcelsToRepack; track parcel.trackingNumber || $index; let i = $index) {
            <tr class="h-12 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
              <td class="px-4 text-xs tabular-nums text-muted-foreground">{{ i + 1 }}</td>
              <td class="px-4 font-mono text-[12.5px] font-medium text-foreground">{{ parcel.trackingNumber || 'N/A' }}</td>
              <td class="px-4 text-muted-foreground">{{ parcel.weight || 'N/A' }} kg</td>
              <td class="px-4 text-xs text-muted-foreground">
                {{ parcel.length || '—' }} × {{ parcel.width || '—' }} × {{ parcel.height || '—' }} cm
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <!-- Carton Details Form Card -->
  <form #repackForm="ngForm" (ngSubmit)="onSubmitNewRepack()">
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border bg-blue-50 dark:bg-blue-500/10 rounded-t-lg">
        <h2 class="text-sm font-semibold text-blue-700 dark:text-blue-300">New Carton Details</h2>
        <p class="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Enter the actual physical measurements for the new carton</p>
      </div>
      <div class="p-4 space-y-5">

        <!-- Carton Number -->
        <div class="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-start sm:gap-x-6">
          <label for="carton_no" class="text-xs font-medium text-muted-foreground pt-2 sm:w-40 shrink-0">
            Carton Number <span class="text-red-500">*</span>
          </label>
          <div>
            <input type="text" id="carton_no" name="carton_no"
              [(ngModel)]="newCarton.carton_no" required
              placeholder="Enter new carton number"
              class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
            <p class="mt-1 text-xs text-muted-foreground">Required — must be unique</p>
          </div>
        </div>

        <!-- Weight -->
        <div class="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-start sm:gap-x-6">
          <label for="carton_weight" class="text-xs font-medium text-muted-foreground pt-2 sm:w-40 shrink-0">
            Carton Weight (kg) <span class="text-red-500">*</span>
          </label>
          <div>
            <input type="number" id="carton_weight" name="carton_weight"
              [(ngModel)]="newCarton.carton_weight" (change)="calculateVolume()"
              placeholder="Enter weight in kg" step="0.01" required
              class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
            @if (originalCarton.carton_weight) {
              <p class="mt-1 text-xs text-muted-foreground">Reference: Original was {{ originalCarton.carton_weight }} kg</p>
            }
          </div>
        </div>

        <!-- Dimensions -->
        <div class="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-start sm:gap-x-6">
          <label class="text-xs font-medium text-muted-foreground pt-2 sm:w-40 shrink-0">
            Dimensions (cm) <span class="text-red-500">*</span>
          </label>
          <div class="grid grid-cols-3 gap-2">
            <div>
              <div class="flex">
                <span class="inline-flex items-center px-2.5 h-9 rounded-l-md border border-r-0 border-border-strong bg-surface-raised text-sm text-muted-foreground">L</span>
                <input type="number" id="carton_length" name="carton_length"
                  [(ngModel)]="newCarton.carton_length" (change)="calculateVolume()"
                  step="0.1" required
                  class="flex-1 min-w-0 h-9 border border-border-strong bg-surface rounded-r-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
              </div>
              @if (originalCarton.carton_length) {
                <p class="mt-1 text-[11px] text-muted-foreground">Ref: {{ originalCarton.carton_length }}</p>
              }
            </div>
            <div>
              <div class="flex">
                <span class="inline-flex items-center px-2.5 h-9 rounded-l-md border border-r-0 border-border-strong bg-surface-raised text-sm text-muted-foreground">W</span>
                <input type="number" id="carton_width" name="carton_width"
                  [(ngModel)]="newCarton.carton_width" (change)="calculateVolume()"
                  step="0.1" required
                  class="flex-1 min-w-0 h-9 border border-border-strong bg-surface rounded-r-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
              </div>
              @if (originalCarton.carton_width) {
                <p class="mt-1 text-[11px] text-muted-foreground">Ref: {{ originalCarton.carton_width }}</p>
              }
            </div>
            <div>
              <div class="flex">
                <span class="inline-flex items-center px-2.5 h-9 rounded-l-md border border-r-0 border-border-strong bg-surface-raised text-sm text-muted-foreground">H</span>
                <input type="number" id="carton_height" name="carton_height"
                  [(ngModel)]="newCarton.carton_height" (change)="calculateVolume()"
                  step="0.1" required
                  class="flex-1 min-w-0 h-9 border border-border-strong bg-surface rounded-r-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
              </div>
              @if (originalCarton.carton_height) {
                <p class="mt-1 text-[11px] text-muted-foreground">Ref: {{ originalCarton.carton_height }}</p>
              }
            </div>
          </div>
        </div>

        <!-- Volume (read-only) -->
        <div class="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-start sm:gap-x-6">
          <label for="carton_volume" class="text-xs font-medium text-muted-foreground pt-2 sm:w-40 shrink-0">
            Carton Volume (cm³)
          </label>
          <div>
            <input type="number" id="carton_volume" name="carton_volume"
              [(ngModel)]="newCarton.carton_volume" readonly
              class="w-full h-9 border border-border bg-surface-raised rounded-md px-3 text-sm text-muted-foreground outline-none cursor-not-allowed" />
            <p class="mt-1 text-xs text-muted-foreground">Auto-calculated from L × W × H</p>
            @if (originalCarton.carton_volume) {
              <p class="mt-0.5 text-xs text-muted-foreground">Reference: Original was {{ originalCarton.carton_volume }} cm³</p>
            }
          </div>
        </div>

        <!-- Dimensions warning -->
        <div class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/15 dark:border-amber-500/30 p-3 text-sm text-amber-800 dark:text-amber-300">
          <ng-icon hlm name="lucideAlertTriangle" class="size-4 mt-0.5 shrink-0" />
          <div>
            <p class="font-semibold mb-1">Enter actual dimensions</p>
            <p class="text-xs">Original carton dimensions are shown as reference only. Enter the actual measurements of the new carton.</p>
            @if (originalCarton.carton_length && originalCarton.carton_width && originalCarton.carton_height) {
              <button type="button" hlmBtn variant="outline" size="sm" class="mt-2 h-7 text-xs"
                (click)="copyOriginalDimensions()">
                <ng-icon hlm name="lucideClipboard" class="mr-1.5 size-3.5" />
                Copy Original Dimensions as Starting Point
              </button>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Sticky footer action bar -->
    <div class="sticky bottom-0 bg-background border-t border-border flex items-center justify-between gap-2 px-4 py-3 mt-4">
      <button type="button" hlmBtn variant="outline" size="sm" (click)="goBack()">
        <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-3.5" />
        Back
      </button>
      <button type="submit" hlmBtn size="sm" [disabled]="repackForm.invalid || isLoading">
        @if (isLoading) {
          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        }
        Update Carton Details
      </button>
    </div>
  </form>

</div>
